<template>
  <div class="reading-stats-dashboard">
    <div class="dashboard-header">
      <h2>Statistik Bacaan</h2>
      <button @click="fetchStats" class="refresh-button" :disabled="loading">
        <font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': loading }" />
        {{ loading ? 'Memuat...' : 'Refresh' }}
      </button>
    </div>

    <div v-if="loading && !stats.total_books" class="loading-container">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      <p>Memuat statistik baca Anda...</p>
    </div>

    <div v-else-if="stats.total_books > 0" class="stats-content">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="stat-card">
          <div class="card-icon books">
            <font-awesome-icon :icon="['fas', 'book']" />
          </div>
          <div class="card-content">
            <div class="card-value">{{ stats.total_books }}</div>
            <div class="card-label">Buku Dibaca</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="card-icon chapters">
            <font-awesome-icon :icon="['fas', 'file-alt']" />
          </div>
          <div class="card-content">
            <div class="card-value">{{ stats.total_chapters }}</div>
            <div class="card-label">Bab Dibaca</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="card-icon time">
            <font-awesome-icon :icon="['fas', 'clock']" />
          </div>
          <div class="card-content">
            <div class="card-value">{{ stats.formatted_read_time }}</div>
            <div class="card-label">Total Waktu Baca</div>
          </div>
        </div>
      </div>

      <!-- Device Distribution & Reading Habits -->
      <div class="charts-section">
        <div class="chart-container">
          <h3><font-awesome-icon :icon="['fas', 'mobile-alt']" /> Distribusi Perangkat</h3>
          <div class="device-distribution">
            <div class="device-bar-container">
              <div class="device-bar-legend">
                <span class="mobile-legend">Mobile ({{ stats.device_distribution.mobile }}%)</span>
                <span class="desktop-legend">Desktop ({{ stats.device_distribution.desktop }}%)</span>
              </div>
              <div class="device-bar">
                <div class="mobile-segment" :style="{width: `${stats.device_distribution.mobile}%`}"></div>
                <div class="desktop-segment" :style="{width: `${stats.device_distribution.desktop}%`}"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="chart-container">
          <h3><font-awesome-icon :icon="['fas', 'sun']" /> Kebiasaan Membaca</h3>
          <div class="reading-habits">
            <div v-for="(value, time) in stats.reading_habits" :key="time" class="habit-item">
              <div class="habit-label">
                <font-awesome-icon :icon="getTimeIcon(time)" />
                {{ getTimeLabel(time) }}
              </div>
              <div class="habit-bar-container">
                <div class="habit-bar" :style="{width: `${value}%`}"></div>
                <span class="habit-percentage">{{ value }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-stats">
      <font-awesome-icon :icon="['fas', 'book-open']" class="no-stats-icon" />
      <p>Belum ada data statistik bacaan.</p>
      <p class="no-stats-hint">Mulai membaca buku untuk melihat statistik di sini.</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReadingStatsDashboard',
  data() {
    return {
      stats: {
        total_books: 0,
        total_chapters: 0,
        total_read_time: 0,
        formatted_read_time: '0 menit',
        device_distribution: {
          mobile: 0,
          desktop: 100
        },
        reading_habits: {
          morning: 0,
          afternoon: 0,
          evening: 0,
          night: 0
        }
      },
      loading: false,
      refreshInterval: null,
      autoRefreshEnabled: true,
      currentRequest: null,
      resizeTimeout: null,
      lastRefreshTime: null,
      chartInstances: {},
      windowWidth: window.innerWidth
    };
  },
  // 1. Tambahkan metode lifecycle created dan beforeUnmount
  created() {
    this.fetchStats();
    
    // Setup auto-refresh data jika diperlukan
    if (this.autoRefreshEnabled) {
      this.setupAutoRefresh();
    }
    
    // Add window resize listener if needed for responsive charts
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    // Clear any timers or intervals
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    
    // Cleanup any active API requests
    if (this.currentRequest) {
      this.currentRequest.cancel();
      this.currentRequest = null;
    }
    
    // Remove any event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Cleanup any chart instances if you're using chart libraries
    if (this.chartInstances) {
      Object.values(this.chartInstances).forEach(chart => {
        if (chart && typeof chart.destroy === 'function') {
          chart.destroy();
        }
      });
    }
  },
  methods: {
    setupAutoRefresh() {
      // Clear any existing refresh interval
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
      
      // Set up new interval - refresh every 5 minutes
      this.refreshInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.fetchStats();
        }
      }, 300000); // 5 minutes in milliseconds
      
      // Also listen for visibility changes to pause/resume refresh
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // User has returned to the tab, refresh data
        this.fetchStats();
      }
    },
    
    handleResize() {
      // Debounce the resize handler
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        // Recreate or resize charts if needed
        this.updateChartsForCurrentSize();
      }, 250);
    },
    
    updateChartsForCurrentSize() {
      // Logic to update charts based on screen size
      // This will depend on which chart library you're using
    },
    
    async fetchStats() {
      // Cancel any in-progress requests
      if (this.currentRequest) {
        this.currentRequest.cancel();
      }
      
      this.loading = true;
      
      try {
        const CancelToken = axios.CancelToken;
        this.currentRequest = CancelToken.source();
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Not authenticated');
        }
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/reading-stats/`, 
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            cancelToken: this.currentRequest.token
          }
        );
        
        this.stats = response.data;
        this.lastRefreshTime = new Date();
        
        // Initialize or update charts with new data
        this.$nextTick(() => {
          this.initializeCharts();
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          // Request dibatalkan (unmount/navigasi) — abaikan
        } else {
          console.error('Failed to fetch reading stats:', error);
          
          if (error.response?.status === 401) {
            this.$router.push('/login');
          }
        }
      } finally {
        this.loading = false;
        this.currentRequest = null;
      }
    }
  },
};
</script>

<style scoped>
.reading-stats-dashboard {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.dashboard-header h2 {
  margin: 0;
  color: #333;
  font-size: 24px;
}

.refresh-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: background-color 0.3s;
}

.refresh-button:hover {
  background-color: #45a049;
}

.refresh-button:disabled {
  background-color: #cccccc;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #666;
}

.loading-icon {
  font-size: 30px;
  color: #4CAF50;
  margin-bottom: 15px;
}

/* Summary Cards */
.summary-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  flex: 1;
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 15px;
  color: white;
  font-size: 20px;
}

.card-icon.books {
  background-color: #4CAF50;
}

.card-icon.chapters {
  background-color: #2196F3;
}

.card-icon.time {
  background-color: #FF9800;
}

.card-content {
  flex: 1;
}

.card-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.card-label {
  font-size: 14px;
  color: #666;
  margin-top: 3px;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-container {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.chart-container h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  color: #333;
}

/* Device Distribution */
.device-distribution {
  padding: 10px;
}

.device-bar-container {
  margin-top: 20px;
}

.device-bar-legend {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.mobile-legend {
  color: #FF9800;
}

.desktop-legend {
  color: #2196F3;
}

.device-bar {
  display: flex;
  height: 30px;
  width: 100%;
  border-radius: 15px;
  overflow: hidden;
  background-color: #f1f1f1;
}

.mobile-segment {
  background-color: #FF9800;
  height: 100%;
}

.desktop-segment {
  background-color: #2196F3;
  height: 100%;
}

/* Reading Habits */
.reading-habits {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.habit-item {
  display: flex;
  align-items: center;
}

.habit-label {
  width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
}

.habit-bar-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.habit-bar {
  height: 14px;
  background-color: #4CAF50;
  border-radius: 7px;
  transition: width 0.5s ease;
}

.habit-percentage {
  font-size: 14px;
  color: #666;
  min-width: 35px;
}

/* No Stats */
.no-stats {
  text-align: center;
  padding: 50px 0;
  color: #666;
}

.no-stats-icon {
  font-size: 48px;
  color: #ddd;
  margin-bottom: 15px;
}

.no-stats-hint {
  font-size: 14px;
  color: #999;
  font-style: italic;
  margin-top: 10px;
}

/* Responsive design */
@media (max-width: 768px) {
  .summary-cards {
    flex-direction: column;
  }
  
  .charts-section {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 15px;
  }
  
  .card-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .card-value {
    font-size: 18px;
  }
  
  .card-label {
    font-size: 12px;
  }
  
  .habit-label {
    width: 120px;
    font-size: 12px;
  }
}
</style>